class CacheService {
  constructor() {
    this.cachePrefix = 'bullbearly-cache-';
    this.defaultExpirationDays = 3; // Default 3 days
  }

  // Generate a cache key from headline
  generateCacheKey(headline) {
    // Create a hash-like key from the headline for consistent caching
    // Use encodeURIComponent to handle non-Latin characters safely
    const encoded = encodeURIComponent(headline);
    const hash = encoded.replace(/[^a-zA-Z0-9]/g, '').substring(0, 50);
    return this.cachePrefix + hash;
  }

  // Check if cache entry exists and is not expired
  isCacheValid(cacheKey) {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return false;

    try {
      const data = JSON.parse(cached);
      const now = new Date();
      const cacheTime = new Date(data.timestamp);
      const daysDiff = (now - cacheTime) / (1000 * 60 * 60 * 24);
      
      return daysDiff < this.defaultExpirationDays;
    } catch (e) {
      console.error('Error parsing cache data:', e);
      return false;
    }
  }

  // Get cached data if valid
  getCachedData(cacheKey) {
    if (!this.isCacheValid(cacheKey)) {
      this.removeCacheEntry(cacheKey);
      return null;
    }

    try {
      const cached = localStorage.getItem(cacheKey);
      const data = JSON.parse(cached);
      console.log('Cache hit for key:', cacheKey);
      return data.data;
    } catch (e) {
      console.error('Error retrieving cached data:', e);
      return null;
    }
  }

  // Store data in cache
  setCachedData(cacheKey, data) {
    try {
      const cacheEntry = {
        timestamp: new Date().toISOString(),
        data: data
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      console.log('Data cached with key:', cacheKey);
    } catch (e) {
      console.error('Error storing cache data:', e);
    }
  }

  // Remove specific cache entry
  removeCacheEntry(cacheKey) {
    localStorage.removeItem(cacheKey);
  }

  // Clean up expired cache entries
  cleanExpiredCache() {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(this.cachePrefix));
    
    let cleanedCount = 0;
    cacheKeys.forEach(key => {
      if (!this.isCacheValid(key)) {
        localStorage.removeItem(key);
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      console.log(`Cleaned up ${cleanedCount} expired cache entries`);
    }
  }

  // Get cache statistics
  getCacheStats() {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(this.cachePrefix));
    
    let validEntries = 0;
    let expiredEntries = 0;
    
    cacheKeys.forEach(key => {
      if (this.isCacheValid(key)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return {
      total: cacheKeys.length,
      valid: validEntries,
      expired: expiredEntries
    };
  }

  // Clear all cache entries
  clearAllCache() {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(this.cachePrefix));
    
    cacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Cleared ${cacheKeys.length} cache entries`);
  }

  // Cache company detection result
  cacheCompanyDetection(headline, companies) {
    const cacheKey = this.generateCacheKey(headline);
    this.setCachedData(cacheKey, {
      type: 'company-detection',
      companies: companies
    });
  }

  // Get cached company detection result
  getCachedCompanyDetection(headline) {
    const cacheKey = this.generateCacheKey(headline);
    const cached = this.getCachedData(cacheKey);
    
    if (cached && cached.type === 'company-detection') {
      return cached.companies;
    }
    
    return null;
  }

  // Cache FinHub stock data
  cacheStockData(symbol, stockData) {
    const cacheKey = this.cachePrefix + 'stock-' + symbol;
    this.setCachedData(cacheKey, {
      type: 'stock-data',
      symbol: symbol,
      data: stockData
    });
  }

  // Get cached stock data
  getCachedStockData(symbol) {
    const cacheKey = this.cachePrefix + 'stock-' + symbol;
    const cached = this.getCachedData(cacheKey);
    
    if (cached && cached.type === 'stock-data' && cached.symbol === symbol) {
      return cached.data;
    }
    
    return null;
  }
}

// Create and export a singleton instance
const cacheService = new CacheService();
export default cacheService;
